import {
  AnimatedContentLoader,
  Button,
  PopupMenu,
  PopupMenuButton,
  PopupMenuContainer,
  Select,
  usePopupMenu,
  VatCodeIcons
} from '@components'
import { getVatRateLabelOptions, VatCode, VatRateLabel } from '@constants'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaPen } from 'react-icons/fa'
import styled from 'styled-components'
import { useBusinessContext } from '@context'
import dayjs from 'dayjs'
import { getVatRate } from '@utils/getVatOptions.tsx'
import { IUpdateTabularAccountProps } from '@pages/AccountsPage/sections'
import { IAccount } from '@query'
import { useVATCodes } from '@root/query/hooks/useVATCodes'

type UpdateData = (data: IUpdateTabularAccountProps) => Promise<void>

interface Props {
  vatRateLabelValue: VatRateLabel
  vatCodeValue: VatCode
  updateData: UpdateData
  account: IAccount
  disabled?: boolean
}

export const EditableVATCell: React.FC<Props> = ({
  vatRateLabelValue,
  vatCodeValue,
  account,
  updateData,
  disabled = false
}) => {
  const { data: business } = useBusinessContext()

  const _updateData = ({ default_vat_rate_label, default_vat_code }) =>
    updateData({
      accountId: account.id,
      data: { default_vat_rate_label, default_vat_code }
    })

  const Icon = VatCodeIcons[vatCodeValue]
  const isZeroValue =
    [VatCode.TAX_FREE, VatCode.DOMESTIC_ZERO_TAX_RATE].indexOf(vatCodeValue) !== -1
  const displayValue = isZeroValue ? 0 : getVatRate(business?.country_config, vatRateLabelValue)

  const handleKeyUp = e => {
    if (e.key === 'Enter' && !disabled) {
      // Simulate mousedown to trigger useOnClickOutside
      const clickEvent = new MouseEvent('mousedown', {
        bubbles: true,
        cancelable: true
      })
      e.currentTarget.dispatchEvent(clickEvent)

      // Open dropdown by click
      e.currentTarget.click()
    }
  }

  return (
    <PopupMenuContainer>
      <PopupMenuButton>
        <StyledCell tabIndex={disabled ? -1 : 0} onKeyUp={handleKeyUp} $disabled={disabled}>
          <span className="percent-wrapper">
            <Icon /> <span className="percent">{displayValue} %</span>
          </span>
          {!disabled && <FaPen />}
        </StyledCell>
      </PopupMenuButton>

      {!disabled && (
        <PopupMenu menuGrow="right">
          <PopupContent>
            <EditForm
              initialVatCode={vatCodeValue}
              initialVatRateLabel={vatRateLabelValue}
              onUpdate={_updateData}
            />
          </PopupContent>
        </PopupMenu>
      )}
    </PopupMenuContainer>
  )
}

type OnUpdateData = (data: {
  default_vat_rate_label: VatRateLabel
  default_vat_code: VatCode
}) => Promise<void>

interface EditFormProps {
  initialVatCode: VatCode
  initialVatRateLabel: VatRateLabel
  onUpdate: OnUpdateData
}

const EditForm: React.FC<EditFormProps> = ({ initialVatCode, initialVatRateLabel, onUpdate }) => {
  const [t] = useTranslation()
  const { toggleVisibility } = usePopupMenu()
  const { data: business } = useBusinessContext()
  const methods = useForm({
    defaultValues: {
      default_vat_code: initialVatCode,
      default_vat_rate_label: initialVatRateLabel
    }
  })
  const { data: vatCodeOptions, isLoading } = useVATCodes()
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = methods

  const onSubmit = handleSubmit(async data => {
    try {
      await onUpdate(data)
      toggleVisibility(false)
    } catch {
      return null
    }
  })

  return (
    <AnimatedContentLoader isLoading={isLoading}>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <Select
            id="accountForm-input-vat"
            label={t('accounts.defaultVat')}
            info={t('accounts.defaultVatInfo')}
            errors={errors.default_vat_code}
            {...register('default_vat_code')}
          >
            {vatCodeOptions?.map(({ label, value, info }) => (
              <option key={value} value={value} title={info}>
                {t(label)}
              </option>
            ))}
          </Select>

          <Select
            info={t('accounts.defaultVatPercentageInfo')}
            label={t('accounts.defaultVatPercentage')}
            errors={errors.default_vat_rate_label}
            {...register('default_vat_rate_label')}
          >
            {getVatRateLabelOptions({
              countryConfig: business?.country_config,
              dateAt: dayjs(),
              t,
              uniqueLabels: true
            }).map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>

          <ButtonContainer>
            <Button
              icon={<FaCheck />}
              isSecondary={true}
              intent="success"
              showSpinner={isSubmitting}
            >
              {t('general.save')}
            </Button>
          </ButtonContainer>
        </form>
      </FormProvider>
    </AnimatedContentLoader>
  )
}

const StyledCell = styled.div<{ $disabled?: boolean }>`
  display: flex;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.sm}rem;
  opacity: ${props => (props.$disabled ? 0.6 : 1)};

  color: ${({ theme }) => theme.colors.neutralBlack};
  .percent-wrapper {
    display: flex;
    gap: ${({ theme }) => theme.spacing.sm}rem;

    & > * {
      align-self: center;
    }
  }

  & > * {
    align-self: center;
  }

  span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  & > svg {
    color: ${({ theme }) => theme.colors.iconGray};
    width: ${({ theme }) => theme.iconSize.xxs}rem;
    height: ${({ theme }) => theme.iconSize.xxs}rem;
    margin-left: ${({ theme }) => theme.spacing.xs}rem;
    opacity: 0;
  }

  &:hover, &:focus {
    color: ${props => (props.$disabled ? 'inherit' : props.theme.colors.primary)};

    & > svg {
      opacity: 1;
    }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const PopupContent = styled.div`
  padding: 1rem;
`
